import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';
import { getOrCreateAnonymousId } from '../services/authStorage';

interface GuestAuthContextType {
  /** true when GuestAuthProvider is in the tree, false from default when absent */
  isGuestAuthActive: boolean;
  /** true only while the guest login API call is in-flight */
  isGuestLoginInProgress: boolean;
  /** set when auto guest login fails — used by splash to show error state */
  guestLoginError: Error | null;
  /** retry function for splash screen retry button */
  retryGuestLogin: () => void;
}

/**
 * Default value used when GuestAuthProvider is absent (magic link mode).
 * isGuestAuthActive: false tells consumers to run their own auth logic.
 */
const GuestAuthContext = createContext<GuestAuthContextType>({
  isGuestAuthActive: false,
  isGuestLoginInProgress: false,
  guestLoginError: null,
  retryGuestLogin: () => {},
});

/**
 * GuestAuthProvider — handles auto guest login on app load.
 *
 * Must be nested inside AuthProvider (needs useAuth()).
 * Triggers guest login when AuthContext finishes loading and isAuthenticated is false.
 * On success, calls AuthContext.login() to store tokens — all auth state stays in AuthContext.
 *
 * Activation: wrap app with <GuestAuthProvider> for auto guest login mode.
 * Deactivation: remove <GuestAuthProvider> to restore magic link mode.
 */
export function GuestAuthProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [isGuestLoginInProgress, setIsGuestLoginInProgress] = useState(false);
  const [guestLoginError, setGuestLoginError] = useState<Error | null>(null);

  // Ref to prevent duplicate API calls (React StrictMode double-invokes effects)
  const isLoginInFlightRef = useRef(false);

  const performGuestLogin = useCallback(async () => {
    if (isLoginInFlightRef.current) return;
    isLoginInFlightRef.current = true;
    setIsGuestLoginInProgress(true);
    setGuestLoginError(null);

    try {
      const anonymousId = getOrCreateAnonymousId();
      const response = await api.guestLogin({ anonymousId });
      login(response);
      console.log('[GuestAuthContext] Auto guest login successful');
    } catch (error) {
      console.error('[GuestAuthContext] Auto guest login failed:', error);
      setGuestLoginError(
        error instanceof Error ? error : new Error('Guest login failed')
      );
    } finally {
      isLoginInFlightRef.current = false;
      setIsGuestLoginInProgress(false);
    }
  }, [login]);

  // Auto-trigger: fire guest login when auth finishes loading and user is not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      performGuestLogin();
    }
  }, [isLoading, isAuthenticated, performGuestLogin]);

  const retryGuestLogin = useCallback(() => {
    performGuestLogin();
  }, [performGuestLogin]);

  const value: GuestAuthContextType = {
    isGuestAuthActive: true,
    isGuestLoginInProgress,
    guestLoginError,
    retryGuestLogin,
  };

  return (
    <GuestAuthContext.Provider value={value}>
      {children}
    </GuestAuthContext.Provider>
  );
}

/**
 * Hook to consume GuestAuthContext.
 * Safe to call whether or not GuestAuthProvider is in the tree —
 * returns default values (isGuestAuthActive: false) when provider is absent.
 */
export function useGuestAuth() {
  return useContext(GuestAuthContext);
}
